import { create } from "zustand";
import { decodeAccessToken, type TokenClaims } from "./token";

// Access token lives in memory only. The refresh token is mirrored to
// localStorage so sessions survive reloads. This is a documented XSS
// trade-off; the recommended hardening is an httpOnly refresh cookie set by
// the backend (see plan, backend prerequisite #3).
const REFRESH_STORAGE_KEY = "nextsub.refresh_token";

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

export type SessionStatus = "loading" | "authenticated" | "anonymous";

interface SessionState {
  status: SessionStatus;
  accessToken: string | null;
  refreshToken: string | null;
  claims: TokenClaims | null;
  /** Persist a freshly issued token pair (after login or refresh). */
  setTokens: (tokens: AuthTokens) => void;
  /** Mark the session resolved with no credentials. */
  setAnonymous: () => void;
  /** Drop all credentials (logout / hard 401). */
  clear: () => void;
}

function readStoredRefresh(): string | null {
  try {
    return localStorage.getItem(REFRESH_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredRefresh(token: string | null): void {
  try {
    if (token) localStorage.setItem(REFRESH_STORAGE_KEY, token);
    else localStorage.removeItem(REFRESH_STORAGE_KEY);
  } catch {
    /* storage unavailable (private mode); session is memory-only */
  }
}

export const useSession = create<SessionState>((set) => ({
  // Start in loading; the AuthProvider resolves to authenticated/anonymous
  // after attempting a refresh with any stored token.
  status: readStoredRefresh() ? "loading" : "anonymous",
  accessToken: null,
  refreshToken: readStoredRefresh(),
  claims: null,

  setTokens: (tokens) => {
    writeStoredRefresh(tokens.refresh_token);
    set({
      status: "authenticated",
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token,
      claims: decodeAccessToken(tokens.access_token),
    });
  },

  setAnonymous: () => {
    writeStoredRefresh(null);
    set({
      status: "anonymous",
      accessToken: null,
      refreshToken: null,
      claims: null,
    });
  },

  clear: () => {
    writeStoredRefresh(null);
    set({
      status: "anonymous",
      accessToken: null,
      refreshToken: null,
      claims: null,
    });
  },
}));

/** Non-reactive access-token getter for the API client middleware. */
export function getAccessToken(): string | null {
  return useSession.getState().accessToken;
}
